import { AlertService } from '@anecdotes/design-system/alert';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, mergeMap, tap } from 'rxjs';
import { HtmlSanitizer } from '@anecdotes/utils';
import { UserFacadeService } from '../../services';
import { UsersAdapterActions } from '../actions';

@Injectable()
export class AlertEffects {
  constructor(
    private actions$: Actions,
    private alertService: AlertService,
    private userFacade: UserFacadeService,
    private translateService: TranslateService,
    private htmlSanitizer: HtmlSanitizer
  ) {}

  roleChangeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.roleChanged),
        mergeMap(async (action) => {
          this.alertService.openAlert({
            alertType: 'success',
            text: this.translateService.instant(this.buildTranslationKey('item.roleDropdown.successToast'), {
              userName: await this.getUserFullName(action.email),
            }),
            closeBtn: true,
            disappearTimeout: 10000,
          });
        }),
      ),
    { dispatch: false },
  );

  roleChangeFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.roleChangeFailed),
        mergeMap(async (action) => {
          this.alertService.openTryAgainActionError({
            errorTranslationKey: this.translateService.instant(
              this.buildTranslationKey('item.roleDropdown.errorToast'),
              {
                userName: await this.getUserFullName(action.email),
              },
            ),
            tryAgainAction: () => this.userFacade.changeUserRoleAsync(action.email, action.newRoleKey),
            disappearTimeout: 10000,
          });
        }),
      ),
    { dispatch: false },
  );

  auditFrameworkChangeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.auditorPermissionsChanged),
        mergeMap(async (action) => {
          this.alertService.openAlert({
            alertType: 'success',
            text: this.translateService.instant(this.buildTranslationKey('auditorPermissions.successToast'), {
              userName: await this.getUserFullName(action.email),
            }),
            closeBtn: true,
            disappearTimeout: 3000,
          });
        }),
      ),
    { dispatch: false },
  );

  auditFrameworkChangeFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.auditorPermissionsChangeFailed),
        mergeMap(async (action) => {
          this.alertService.openTryAgainActionError({
            errorTranslationKey: this.translateService.instant(
              this.buildTranslationKey('auditorPermissions.errorToast'),
              {
                userName: await this.getUserFullName(action.email),
              },
            ),
            tryAgainAction: () =>
              this.userFacade.changeAuditorPermissionsAsync(action.email, action.newAuditFrameworks),
          });
        }),
      ),
    { dispatch: false },
  );

  userRemovalSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.userRemovedSuccessfully),
        tap((action) => {
          this.alertService.openAlert({
            alertType: 'success',
            text: this.translateService.instant(this.buildTranslationKey('userToasts.remove.success'), {
              email: action.email,
            }),
            closeBtn: true,
            disappearTimeout: 3000,
          });
        }),
      ),
    { dispatch: false },
  );

  userRemovalFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.userRemovalFailed),
        tap((action) => {
          this.alertService.openTryAgainActionError({
            errorTranslationKey: this.translateService.instant(this.buildTranslationKey('userToasts.remove.fail'), {
              email: action.email,
            }),
            tryAgainAction: () => this.userFacade.removeUser(action.email),
          });
        }),
      ),
    { dispatch: false },
  );

  userInvitationResent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.userInvitationResent),
        tap((action) => {
          this.alertService.openAlert({
            alertType: 'success',
            text: this.translateService.instant(this.buildTranslationKey('userToasts.invitationResent.success'), {
              email: action.email,
            }),
            disappearTimeout: 3000,
          });
        }),
      ),
    { dispatch: false },
  );

  emailLoginChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.emailLoginChangedSuccessfully),
        tap((action) => {
          this.alertService.openAlert({
            alertType: 'success',
            text: this.translateService.instant(this.buildTranslationKey('userToasts.emailLoginChanged.success'), {
              email: action.email,
            }),
            disappearTimeout: 3000,
          });
        }),
      ),
    { dispatch: false },
  );

  emailLoginChangeFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAdapterActions.emailLoginChangeFailed),
        tap((action) => {
          this.alertService.openAlert({
            alertType: 'error',
            text: this.translateService.instant(this.buildTranslationKey('userToasts.emailLoginChanged.failure'), {
              email: action.email,
            }),
            disappearTimeout: 3000,
          });
        }),
      ),
    { dispatch: false },
  );

  private async getUserFullName(email: string): Promise<string> {
    const user = await firstValueFrom(this.userFacade.getUserByEmail(email));

    return this.htmlSanitizer.encodeTextToHtml(`${user.first_name} ${user.last_name}`);
  }

  private buildTranslationKey(relativeKey: string): string {
    return `userManagement.${relativeKey}`;
  }
}
